import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Users, Building2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { RegionalCommandBoard } from './RegionalCommandBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Staff-picker board — only lists staff of one role and drives the "who am I
// assigning to right now" selection. The Communities selection lives above,
// shared across both boards, so this component stays purposefully thin.
const StaffBoard = ({
  title,
  roleLabel,
  staff,
  assignments,
  activeUserId,
  onSelect,
  onSave,
  onCopyFrom,
  saving,
  saveDisabled,
  saveCommunityCount,
  testid,
}) => {
  // "Copy assignment from …" quick-fill. Any teammate (across roles) with
  // already-saved communities can be used as a template so onboarding a new
  // crew leader / site agent is a two-click stamp instead of hand-picking
  // dozens of communities. Only shows when a target staff is selected.
  const templates = assignments.filter(
    (a) => (a.assigned_properties || []).length > 0 && a.user_id !== activeUserId,
  );
  return (
    <Card className="bg-zinc-800/60 border-zinc-700 p-4" data-testid={testid}>
      <h4 className="font-bold text-sm text-white mb-3 flex items-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        <Users className="h-4 w-4 mr-1.5 text-blue-400" />
        {title}
        <Badge className="ml-2 bg-zinc-700 text-zinc-200 text-[10px]">{staff.length}</Badge>
      </h4>

      {staff.length === 0 ? (
        <p className="text-xs text-zinc-500">No {roleLabel}s in your region.</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1 mb-3" data-testid={`${testid}-list`}>
          {staff.map((s) => {
            const asg = assignments.find((a) => a.user_id === s.user_id);
            const active = activeUserId === s.user_id;
            return (
              <button
                key={s.user_id}
                type="button"
                onClick={() => onSelect(s.user_id)}
                className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
                  active ? 'bg-blue-600/25 border-blue-500 text-white' : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-blue-500'
                }`}
                data-testid={`${testid}-staff-${s.user_id}`}
              >
                <span className="font-bold">{s.name}</span>
                {s.badge_number && <span className="text-zinc-500 ml-1.5">{s.badge_number}</span>}
                <span className="block text-[10px] text-zinc-400 mt-0.5">
                  {asg?.assigned_properties?.length
                    ? `${asg.assigned_properties.length} communities assigned`
                    : 'No communities assigned yet'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {activeUserId && (
        <div className="space-y-2">
          {templates.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
                <Copy className="h-2.5 w-2.5" /> Copy assignment from teammate
              </p>
              <Select
                onValueChange={(uid) => uid && onCopyFrom(uid)}
              >
                <SelectTrigger
                  className="bg-zinc-900 border-zinc-700 h-8 text-xs text-zinc-200"
                  data-testid={`${testid}-copy-from-trigger`}
                >
                  <SelectValue placeholder="Choose a template teammate…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-200 max-h-60">
                  {templates.map((t) => (
                    <SelectItem
                      key={t.user_id}
                      value={t.user_id}
                      className="text-xs"
                      data-testid={`${testid}-copy-from-${t.user_id}`}
                    >
                      {t.user_name} · {(t.assigned_properties || []).length} communities
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            onClick={onSave}
            disabled={saving || saveDisabled}
            className="w-full bg-blue-600 text-white hover:opacity-90 font-bold h-8 text-xs"
            data-testid={`${testid}-save-btn`}
          >
            {saving
              ? 'Saving…'
              : `Save ${roleLabel} · ${saveCommunityCount} ${saveCommunityCount === 1 ? 'community' : 'communities'}`}
          </Button>
        </div>
      )}
    </Card>
  );
};

// Supervisor view: 1) Scope Cities filter → 2) Communities selection scoped by
// those cities → 3) two staff boards (Crew Leaders left · Site Agents right)
// used to pick who receives that community set on save.
export const HierarchyAssignments = ({ user }) => {
  const [teamView, setTeamView] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  // Feb 2026 iter-77 — Cities filter + Communities selection are both
  // hoisted to the parent so a single top-down flow drives BOTH boards.
  const [sharedCityFilter, setSharedCityFilter] = useState([]);
  const [sharedPropertyIds, setSharedPropertyIds] = useState([]);
  // { boardKey: 'crew_leader' | 'agent', userId }
  const [activeStaff, setActiveStaff] = useState({ boardKey: null, userId: null });

  const load = useCallback(async () => {
    try {
      const [tvRes, asgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/company/staff-assignments`, { credentials: 'include' }),
      ]);
      if (tvRes.ok) {
        const data = await tvRes.json();
        // Iter-78 pt 8 — scope to the caller's own region_id when set (RM /
        // supervisor / crew leader all belong to one region). Fall back to
        // the first non-unassigned entry only if the user has no region_id
        // (rare — admin / CEO). This keeps R07 users on R07 data instead of
        // whichever region happens to sort first.
        const entries = (data || []).filter((e) => e.region?.region_id && e.region.region_id !== '_unassigned');
        const own = user?.region_id
          ? entries.find((e) => e.region.region_id === user.region_id)
          : null;
        setTeamView(own || entries[0] || null);
      }
      if (asgRes.ok) setAssignments(await asgRes.json());
    } catch (e) {
      console.error('Failed to load hierarchy assignments:', e);
    }
  }, [user?.region_id]);

  useEffect(() => { load(); }, [load]);

  const region = teamView?.region;
  const dedupe = (arr, key) => arr.filter((x, i, a) => a.findIndex((y) => y[key] === x[key]) === i);
  const staff = useMemo(
    () => dedupe(teamView?.staff || [], 'user_id').filter((s) => s.user_id !== user?.user_id && !assignments.some(a => a.user_id === s.user_id && a.regional_order_id)),
    [teamView, user?.user_id, assignments],
  );
  const properties = useMemo(
    () => dedupe(teamView?.properties || [], 'property_id'),
    [teamView],
  );

  const scopeCities = useMemo(
    () => [...new Set(properties.map((p) => (p.city || '').trim()).filter(Boolean))].sort(),
    [properties],
  );
  const scopedProps = useMemo(
    () => (sharedCityFilter.length === 0
      ? properties
      : properties.filter((p) => sharedCityFilter.includes((p.city || '').trim()))),
    [properties, sharedCityFilter],
  );

  const toggleCity = (c) =>
    setSharedCityFilter((prev) => (prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c]));

  const toggleProperty = (pid) =>
    setSharedPropertyIds((prev) => (prev.includes(pid) ? prev.filter((v) => v !== pid) : [...prev, pid]));

  // Whenever the caller narrows the city filter, drop any selected
  // community that fell out of scope so save can't smuggle it back in.
  // Drop out-of-scope community selections whenever the city filter or the
  // available property set changes. `scopedProps` is memoized so this only
  // runs on meaningful changes (not on every render).
  useEffect(() => {
    setSharedPropertyIds((prev) => {
      const next = prev.filter((pid) => scopedProps.some((p) => p.property_id === pid));
      return next.length === prev.length ? prev : next;
    });
  }, [scopedProps]);

  const selectStaff = (boardKey, userId) => {
    setActiveStaff({ boardKey, userId });
    const existing = assignments.find((a) => a.user_id === userId);
    // Preload their currently-assigned communities (only those still in scope)
    // so the reviewer sees the current state and can just toggle deltas.
    const preload = (existing?.assigned_properties || []).filter((pid) =>
      scopedProps.some((p) => p.property_id === pid),
    );
    setSharedPropertyIds(preload);
  };

  // Copy assignment from template teammate — replaces the current shared
  // property selection with the source teammate's assigned communities
  // (intersected with the currently in-scope set).
  const copyAssignmentFrom = (sourceUserId) => {
    const source = assignments.find((a) => a.user_id === sourceUserId);
    if (!source) { toast.error('Source assignment not found.'); return; }
    const inScope = (source.assigned_properties || []).filter((pid) =>
      scopedProps.some((p) => p.property_id === pid),
    );
    setSharedPropertyIds(inScope);
    toast.success(`Copied ${inScope.length} communities from ${source.user_name}.`);
  };

  const save = async (boardKey) => {
    if (!activeStaff.userId || activeStaff.boardKey !== boardKey) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/staff-assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: activeStaff.userId,
          assigned_cities: sharedCityFilter,
          assigned_properties: sharedPropertyIds,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to save assignment');
      toast.success(`Assignment saved for ${data.user_name}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const selectAllInScope = () => setSharedPropertyIds(scopedProps.map((p) => p.property_id));
  const clearProperties = () => setSharedPropertyIds([]);

  return (
    <Card className="bg-zinc-900 border-2 p-6 mb-2" style={{ borderColor: 'rgba(59, 130, 246, 0.5)' }} data-testid="hierarchy-assignments-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Users className="h-5 w-5 mr-2 text-blue-400" />
          HIERARCHY ASSIGNMENTS · {region ? `${region.region_code} ${region.name}` : '—'}
        </h3>
        <span className="text-xs text-zinc-400">
          1. Pick cities · 2. Pick communities · 3. Assign to Crew Leader / Site Agent.
        </span>
      </div>

      {!region ? (
        <p className="text-zinc-400 text-sm" data-testid="hierarchy-no-region">
          No region assigned to your profile yet — ask HR/Admin to set your region.
        </p>
      ) : (
        <>
          {/* Step 1 — Scope Cities (drives Communities panel below + both boards). */}
          <Card
            className="bg-zinc-900/80 border border-blue-500/40 p-4 mb-3"
            data-testid="hierarchy-scope-cities-panel"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <h4
                className="font-bold text-sm text-blue-300"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.06em' }}
              >
                1 · SCOPE CITIES
              </h4>
              <span className="text-[11px] text-zinc-400">
                {sharedCityFilter.length === 0
                  ? `all cities · ${scopedProps.length} communities in scope`
                  : `${sharedCityFilter.length} ${sharedCityFilter.length === 1 ? 'city' : 'cities'} · ${scopedProps.length} ${scopedProps.length === 1 ? 'community' : 'communities'} in scope`}
              </span>
            </div>
            {scopeCities.length === 0 ? (
              <p className="text-xs text-zinc-500">No properties in your region yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="hierarchy-scope-cities-chips">
                {scopeCities.map((c) => {
                  const active = sharedCityFilter.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCity(c)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                        active
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-blue-500'
                      }`}
                      data-testid={`hierarchy-scope-city-${c.replace(/\s+/g, '-')}`}
                    >
                      {c}
                    </button>
                  );
                })}
                {sharedCityFilter.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSharedCityFilter([])}
                    className="px-2 py-0.5 rounded-full text-[10px] text-zinc-400 border border-zinc-700 hover:border-zinc-500"
                    data-testid="hierarchy-scope-cities-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </Card>

          {/* Step 2 — Communities selection (scoped by Step 1, shared by both boards). */}
          <Card
            className="bg-zinc-900/80 border border-amber-500/40 p-4 mb-4"
            data-testid="hierarchy-communities-panel"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <h4
                className="font-bold text-sm text-amber-300 flex items-center gap-1.5"
                style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.06em' }}
              >
                <Building2 className="h-4 w-4" />
                2 · COMMUNITIES
              </h4>
              <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                <span data-testid="hierarchy-communities-count">
                  {sharedPropertyIds.length} of {scopedProps.length} selected
                </span>
                {scopedProps.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={selectAllInScope}
                      className="px-2 py-0.5 rounded-full border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-[10px] font-bold"
                      data-testid="hierarchy-communities-select-all"
                    >
                      Select all in scope
                    </button>
                    <button
                      type="button"
                      onClick={clearProperties}
                      className="px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-400 hover:border-zinc-500 text-[10px]"
                      data-testid="hierarchy-communities-clear"
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            {scopedProps.length === 0 ? (
              <p className="text-xs text-zinc-500" data-testid="hierarchy-communities-empty">
                {sharedCityFilter.length === 0
                  ? 'No communities in your region yet.'
                  : 'No communities in the selected cities — clear the city filter or pick different cities.'}
              </p>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1"
                data-testid="hierarchy-communities-list"
              >
                {scopedProps.map((p) => {
                  const on = sharedPropertyIds.includes(p.property_id);
                  return (
                    <button
                      key={p.property_id}
                      type="button"
                      onClick={() => toggleProperty(p.property_id)}
                      className={`text-left px-2.5 py-1.5 rounded-md text-xs border transition-colors flex items-center justify-between gap-2 ${
                        on
                          ? 'bg-amber-500/15 border-amber-400 text-amber-100'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-amber-500'
                      }`}
                      data-testid={`hierarchy-community-${p.property_id}`}
                      data-selected={on ? 'true' : 'false'}
                    >
                      <span className="min-w-0">
                        <span className="font-bold block truncate">{p.property_name}</span>
                        <span className="text-[10px] text-zinc-500 block truncate">{p.city || '—'}</span>
                      </span>
                      <Badge className="bg-zinc-700 text-zinc-200 text-[10px] flex-shrink-0">
                        {p.total_units ?? '—'} units
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Iter-78 pt 5 · Regional Command 3-column board — pinned right
              under Communities so RM sees Supervisors / Crew Leaders / Site
              Agents at a glance. Clicking a Supervisor filters columns 2 & 3
              to that supervisor's chain (Regional Assignment Card preview). */}
          {['manager', 'company_manager', 'admin'].includes(user?.role) && (
            <RegionalCommandBoard user={user} embedded />
          )}

          {/* Step 3 — Role-aware delegation boards.
               • Regional Manager (role=manager) delegates to Supervisors
                 (main role — carves the region into supervisor scopes that
                 populate as "Regional Assignments" cards on the supervisor
                 dashboards).
               • Supervisor delegates to Crew Leaders (left) and Site Agents (right). */}
          <div className="grid lg:grid-cols-2 gap-4" data-testid="hierarchy-boards-grid">
            {['manager', 'company_manager', 'admin'].includes(user?.role) ? (
              <>
                <StaffBoard
                  title="4 · DELEGATE TO A SUPERVISOR"
                  roleLabel="Supervisor"
                  staff={staff.filter((s) => s.role === 'supervisor')}
                  assignments={assignments}
                  activeUserId={activeStaff.boardKey === 'supervisor' ? activeStaff.userId : null}
                  onSelect={(uid) => selectStaff('supervisor', uid)}
                  onSave={() => save('supervisor')}
                  onCopyFrom={copyAssignmentFrom}
                  saving={saving}
                  saveDisabled={sharedPropertyIds.length === 0}
                  saveCommunityCount={sharedPropertyIds.length}
                  testid="supervisors-board"
                />
                <Card className="bg-zinc-800/40 border border-dashed border-zinc-700 p-4 text-xs text-zinc-500" data-testid="hierarchy-manager-hint">
                  <p className="font-bold text-zinc-300 mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    HANDING OFF TO SUPERVISORS
                  </p>
                  <p className="leading-snug">
                    Every supervisor you save above receives a <span className="text-[#C9A962] font-bold">Regional Assignment card</span> on
                    their dashboard — cities &amp; communities you carved for them. From that card they then assign their crew leaders and
                    site agents (who schedule properties in order by building on their crew-leader card).
                  </p>
                  <p className="leading-snug mt-2 text-zinc-600">
                    Up to 6 supervisors per region · You own the whole region &amp; can override any supervisor&apos;s scope at any time.
                  </p>
                </Card>
              </>
            ) : (
              <>
                <StaffBoard
                  title="3A · CREW LEADERS BOARD"
                  roleLabel="Crew Leader"
                  staff={staff.filter((s) => s.role === 'crew_leader')}
                  assignments={assignments}
                  activeUserId={activeStaff.boardKey === 'crew_leader' ? activeStaff.userId : null}
                  onSelect={(uid) => selectStaff('crew_leader', uid)}
                  onSave={() => save('crew_leader')}
                  onCopyFrom={copyAssignmentFrom}
                  saving={saving}
                  saveDisabled={sharedPropertyIds.length === 0}
                  saveCommunityCount={sharedPropertyIds.length}
                  testid="crew-leaders-board"
                />
                <StaffBoard
                  title="3B · SITE AGENTS BOARD"
                  roleLabel="Site Agent"
                  staff={staff.filter((s) => s.role === 'agent')}
                  assignments={assignments}
                  activeUserId={activeStaff.boardKey === 'agent' ? activeStaff.userId : null}
                  onSelect={(uid) => selectStaff('agent', uid)}
                  onSave={() => save('agent')}
                  onCopyFrom={copyAssignmentFrom}
                  saving={saving}
                  saveDisabled={sharedPropertyIds.length === 0}
                  saveCommunityCount={sharedPropertyIds.length}
                  testid="site-agents-board"
                />
              </>
            )}
          </div>

          {/* Iter-78 pt 8 — RM cascade: once a Supervisor is picked in "4 ·
              DELEGATE TO A SUPERVISOR", surface a second row with Crew
              Leader + Site Agent boards so the RM can push assignments
              all the way down the chain without leaving the tab. Both
              boards are scoped to the picked supervisor's downstream chain
              via `managed_by`. */}
          {['manager', 'company_manager', 'admin'].includes(user?.role)
            && activeStaff.boardKey === 'supervisor'
            && activeStaff.userId && (
            <div
              className="grid lg:grid-cols-2 gap-4 mt-4"
              data-testid="hierarchy-manager-cascade"
            >
              <StaffBoard
                title="5 · ASSIGN CREW LEADERS"
                roleLabel="Crew Leader"
                staff={staff.filter((s) =>
                  s.role === 'crew_leader'
                  && (assignments.find((a) => a.user_id === s.user_id)?.managed_by === activeStaff.userId
                      || !assignments.find((a) => a.user_id === s.user_id)?.managed_by)
                )}
                assignments={assignments}
                activeUserId={activeStaff.boardKey === 'crew_leader' ? activeStaff.userId : null}
                onSelect={(uid) => selectStaff('crew_leader', uid)}
                onSave={() => save('crew_leader')}
                onCopyFrom={copyAssignmentFrom}
                saving={saving}
                saveDisabled={sharedPropertyIds.length === 0}
                saveCommunityCount={sharedPropertyIds.length}
                testid="cascade-crew-leaders-board"
              />
              <StaffBoard
                title="6 · ASSIGN SITE AGENTS"
                roleLabel="Site Agent"
                staff={staff.filter((s) => {
                  if (s.role !== 'agent') return false;
                  const mgr = assignments.find((a) => a.user_id === s.user_id)?.managed_by;
                  if (!mgr) return true; // unassigned agents visible for first-time stamp
                  // Agent's manager (a crew leader) must be managed by the picked supervisor.
                  const mgrRow = assignments.find((a) => a.user_id === mgr);
                  return !mgrRow || mgrRow.managed_by === activeStaff.userId;
                })}
                assignments={assignments}
                activeUserId={activeStaff.boardKey === 'agent' ? activeStaff.userId : null}
                onSelect={(uid) => selectStaff('agent', uid)}
                onSave={() => save('agent')}
                onCopyFrom={copyAssignmentFrom}
                saving={saving}
                saveDisabled={sharedPropertyIds.length === 0}
                saveCommunityCount={sharedPropertyIds.length}
                testid="cascade-site-agents-board"
              />
            </div>
          )}

          {/* Iter-77 · The flat "Assigned Crews" text table has been removed
              — the same information is now rendered as a dashboard-card grid
              by the Regional Assignments section that sits right below this
              component in StaffDashboard (mirror of the region → communities
              → assigned-crew card the supervisor already knows). */}
        </>
      )}
    </Card>
  );
};
